import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ANIMATION_MS = 1800;
// The headline copy is plural ("N listings"), so the spin starts at 2 and never passes through "1 listings" on its way to the total.
const FIRST_VALUE = 2;

type Props = {
  target: number;
};

// Quartic ease-in-out, the wheel curve: it pulls away from 2 slowly, spends the
// middle of the run at speed, then brakes onto the total. A cubic is the same
// shape with less of everything, and at this duration it read as flat.
const easeInOut = (t: number) =>
  t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;

/**
 * The listing total, spun up to `target` like a wheel: away from 2 slowly, then
 * accelerating, then braking onto the number. Formatted for the active locale
 * (fi groups with a space, en with a comma).
 *
 * The tween runs on requestAnimationFrame, so the value moves with the display
 * rather than on a timer that drifts against it.
 *
 * `target` moves: the panel renders a fallback while GET /listings/count is in
 * flight and swaps in the live number when it lands. The tween reads the target
 * through a ref instead of restarting on it, so the swap bends the curve it is
 * already on. Restarting produced two eases back to back, which is what stopped
 * it reading as one ease-in-out. Once the tween has landed (or was skipped for
 * reduced motion) the value renders `target` directly, so a total that arrives
 * late still replaces the fallback.
 *
 * The hidden sizer holds the final width from the first frame, so the count
 * grows into reserved space and nothing around it reflows.
 */
const ListingCountUp = ({ target }: Props) => {
  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith('fi') ? 'fi-FI' : 'en-US';
  const formatted = target.toLocaleString(locale);
  const [displayed, setDisplayed] = useState(FIRST_VALUE);
  const [finished, setFinished] = useState(false);
  const targetRef = useRef(target);

  useEffect(() => {
    targetRef.current = target;
  }, [target]);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setFinished(true);
      return;
    }

    let frame = 0;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min(1, (now - start) / ANIMATION_MS);
      setDisplayed(
        Math.round(FIRST_VALUE + (targetRef.current - FIRST_VALUE) * easeInOut(progress)),
      );
      if (progress < 1) frame = requestAnimationFrame(step);
      else setFinished(true);
    };
    frame = requestAnimationFrame(step);

    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <span className="signup-login-modal__count">
      <span className="signup-login-modal__count__sizer" aria-hidden="true">
        {formatted}
      </span>
      <span className="signup-login-modal__count__value">
        {(finished ? target : displayed).toLocaleString(locale)}
      </span>
    </span>
  );
};

export default ListingCountUp;
